import React, { useState, useEffect, useCallback, useContext } from "react";
import {
	Box,
	Divider,
	IconButton,
	Badge,
	ListItemButton,
	ListItemAvatar,
	Avatar,
	ListItemText,
	ListItemSecondaryAction,
	CssBaseline,
	Popover,
	List,
	ListItem,
	Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import NotificationIcon from "@mui/icons-material/NotificationsOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";

import { NotificationsPopover } from "./NotificationsPopover";
import { useNotifications } from "@/hooks/useNotifications";
import { UserMenu } from "./UserMenu";
import { NavigationDrawer } from "./NavigationDrawer";
import Search from "@components/search";
import { MaterialUISwitch } from "@/components/Switch";
import Customizer from "@/components/customize/customizer";
import { ColorModeContext } from "@/App";
import { useAuth } from "@/hooks/auth";
import { ChatWidget } from "@/components/chat/ChatWidget";
import { AnnouncementsPopover } from "./AnnouncementsPopover";
import { AnnouncementDrawer } from "@/components/announcements/AnnouncementDrawer";
import { useAnnouncements, Announcement } from "@/hooks/useAnnouncements";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { Button } from "@mui/material";

import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED, HEADER_HEIGHT } from "./config";

export const drawerWidth = DRAWER_WIDTH;
export const drawerWidthCollapsed = DRAWER_WIDTH_COLLAPSED;
export const headerHeight = HEADER_HEIGHT;

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [isNavOpen, setIsNavOpen] = useState(() => {
		const saved = localStorage.getItem("isNavOpen");
		return saved !== null ? saved === "true" : true;
	});

	useEffect(() => {
		localStorage.setItem("isNavOpen", String(isNavOpen));
	}, [isNavOpen]);
	const { user, isLoading: loading } = useAuth();
	const [changingDesign, setChangingDesign] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [notificationAnchorEl, setNotificationAnchorEl] =
		useState<null | HTMLElement>(null);
	const [announcementAnchorEl, setAnnouncementAnchorEl] =
		useState<null | HTMLElement>(null);
	const [selectedAnnouncement, setSelectedAnnouncement] =
		useState<Announcement | null>(null);
	const [isAnnouncementDrawerOpen, setIsAnnouncementDrawerOpen] =
		useState(false);
	const [unreadAnnouncementsCount, setUnreadAnnouncementsCount] = useState(0);
	const { announcements, fetchAnnouncements } = useAnnouncements();

	useEffect(() => {
		if (user) {
			fetchAnnouncements({ status: "ACTIVE", limit: 10 });
		}
	}, [user, fetchAnnouncements]);

	useEffect(() => {
		const lastRead = localStorage.getItem("lastReadAnnouncementAt");
		if (announcements.length > 0) {
			if (!lastRead) {
				setUnreadAnnouncementsCount(announcements.length);
			} else {
				const count = announcements.filter(
					(a) =>
						new Date(a.publishedAt || a.createdAt).getTime() >
						parseInt(lastRead),
				).length;
				setUnreadAnnouncementsCount(count);
			}
		}
	}, [announcements]);

	const theme = useTheme();
	const location = useLocation();
	const snackbar = useSnackbar();
	const colorMode = useContext(ColorModeContext);
	const {
		notifications,
		unreadCount,
		loading: notificationsLoading,
		markAsRead,
		markAllAsRead,
	} = useNotifications();

	const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
		setNotificationAnchorEl(event.currentTarget);
	};

	const handleNotificationClose = () => {
		setNotificationAnchorEl(null);
	};

	const handleAnnouncementOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnnouncementAnchorEl(event.currentTarget);
		localStorage.setItem("lastReadAnnouncementAt", Date.now().toString());
		setUnreadAnnouncementsCount(0);
	};

	const handleAnnouncementClose = () => {
		setAnnouncementAnchorEl(null);
	};

	const openAnnouncementDrawer = (announcement: Announcement) => {
		setSelectedAnnouncement(announcement);
		setIsAnnouncementDrawerOpen(true);
	};

	return (
		<Box sx={{ display: "flex" }}>
			<CssBaseline />
			<UserMenu
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				onChangingDesign={() => setChangingDesign(true)}
				remainingSessionTime={user?.sessionTimeRemaining}
			/>
			{changingDesign && (
				<Customizer
					open={changingDesign}
					onClose={() => setChangingDesign(false)}
					onSuccess={() => window.location.reload()}
				/>
			)}
			<NotificationsPopover
				anchorEl={notificationAnchorEl}
				onClose={handleNotificationClose}
				notifications={notifications}
				loading={notificationsLoading}
				markAsRead={markAsRead}
				markAllAsRead={markAllAsRead}
			/>
			<AnnouncementsPopover
				anchorEl={announcementAnchorEl}
				onClose={handleAnnouncementClose}
				onOpenAnnouncement={openAnnouncementDrawer}
			/>
			<AnnouncementDrawer
				open={isAnnouncementDrawerOpen}
				announcement={selectedAnnouncement}
				onClose={() => setIsAnnouncementDrawerOpen(false)}
			/>
			<NavigationDrawer
				open={isNavOpen}
				headerHeight={headerHeight}
				navigations={user?.navigations || []}
				activePath={location.pathname}
			/>
			<Box
				component="main"
				sx={{
					display: "flex",
					flexDirection: "column",
					flexGrow: 1,
					minWidth: 0,
					//marginLeft: isNavOpen ? `${drawerWidth}px` : `${drawerWidthCollapsed}px`,
					transition: theme.transitions.create("margin", {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.leavingScreen,
					}),
				}}
			>
				<Box
					id="header"
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						height: headerHeight,
						px: 3,
						position: "sticky",
						top: 0,
						zIndex: theme.zIndex.appBar,
						backgroundColor: alpha(theme.palette.background.default, 0.5),
						backdropFilter: "blur(10px)",
						borderBottom: `1px solid ${theme.palette.divider}`,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
						<IconButton onClick={() => setIsNavOpen(!isNavOpen)} edge="start">
							{isNavOpen ? <CloseIcon /> : <MenuIcon />}
						</IconButton>
						<Search placeholder="Хайлт хийх... (Ctrl+K)" width="half" />
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<IconButton
							onClick={colorMode.toggleColorMode}
							size="small"
							sx={{
								color: theme.palette.mode === "dark" ? "white" : "#0b192c",
								border: "1px solid",
								borderColor: "divider",
							}}
						>
							{theme.palette.mode === "dark" ? (
								<LightModeIcon fontSize="small" />
							) : (
								<DarkModeIcon fontSize="small" />
							)}
						</IconButton>
						<IconButton
							size="small"
							onClick={handleNotificationOpen}
							sx={{
								color: theme.palette.mode === "dark" ? "white" : "#0b192c",
								border: "1px solid",
								borderColor: "divider",
							}}
						>
							<Badge badgeContent={unreadCount} color="error">
								<NotificationIcon fontSize="small" />
							</Badge>
						</IconButton>
						<ChatWidget />
						<Button
							variant="contained"
							onClick={handleAnnouncementOpen}
							sx={{
								bgcolor: "#0b192c",
								color: "white",
								borderRadius: 2,
								textTransform: "none",
								fontWeight: "bold",
								ml: 0.5,
								"&:hover": { bgcolor: "#122643" },
							}}
						>
							зарлалууд
							{unreadAnnouncementsCount > 0 && (
								<Box
									sx={{
										ml: 1,
										bgcolor: "error.main",
										color: "white",
										borderRadius: "10px",
										px: 0.8,
										py: 0.2,
										fontSize: "0.75rem",
										lineHeight: 1,
									}}
								>
									{unreadAnnouncementsCount}
								</Box>
							)}
						</Button>
						<Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1.5 }} />
						<ListItemButton
							dense
							sx={{
								borderRadius: 3,
								maxWidth: 300,
								width: { xs: "auto", md: 300 },
							}}
							onClick={(e) => setAnchorEl(e.currentTarget)}
						>
							<ListItemAvatar>
								<Avatar
									src={user?.avatar ?? "https://placehold.co/150x150"}
									alt="user"
								/>
							</ListItemAvatar>
							<Box sx={{ display: { xs: "none", md: "block" } }}>
								<ListItemText
									primary={user?.firstName}
									secondary={"системхэрэглэгч"}
								/>
							</Box>
							<ListItemSecondaryAction
								sx={{ display: { xs: "none", md: "block" } }}
							>
								<ExpandMore />
							</ListItemSecondaryAction>
						</ListItemButton>
					</Box>
				</Box>
				<Divider />
				<Box sx={{ p: 3, backgroundColor: "background.default", flexGrow: 1 }}>
					{children}
				</Box>
			</Box>
		</Box>
	);
};

export default MainLayout;
